import React from 'react'

import { Elements } from '@stripe/react-stripe-js'

import { PaymentMethodType } from './PaymentMethods.types'
import { StripeCardForm } from './StripeCardForm'
import { StripeCheckoutForm } from './StripeCheckoutForm'

interface StripeFormRendererProps {
	paymentMethodType: PaymentMethodType
	cardPaymentMethodId: number | null
	accountId: number
	billingSystemCountry?: string
	stripePromise: any
	clientSecret: string | null
	getElementsOptions: () => any
	availablePaymentMethods: any
	isLoadingPaymentMethods: boolean
	isCreatingCardIntent: boolean
	isCreatingSepaIntent: boolean
	selectedStripePaymentMethodType: 'CARD' | 'SEPA' | null
	handleSelectPaymentMethod: (
		paymentPlatformMethodId: number,
		type: 'CARD' | 'SEPA'
	) => Promise<void>
	handleStripeCardSubmit: (
		paymentIntentId: string,
		paymentMethodId: string
	) => Promise<void>
	onSuccess: () => void
	onCancel: () => void
	onSubmittingChange?: (isSubmitting: boolean) => void
	formId?: string
	hideInternalButtons?: boolean
}

/**
 * Component responsible for rendering the appropriate Stripe form
 * based on the payment method type and available payment methods
 */
export const StripeFormRenderer: React.FC<StripeFormRendererProps> = ({
	paymentMethodType,
	cardPaymentMethodId,
	accountId,
	billingSystemCountry,
	stripePromise,
	clientSecret,
	getElementsOptions,
	availablePaymentMethods,
	isLoadingPaymentMethods,
	isCreatingCardIntent,
	isCreatingSepaIntent,
	selectedStripePaymentMethodType,
	handleSelectPaymentMethod,
	handleStripeCardSubmit,
	onSuccess,
	onCancel,
	onSubmittingChange,
	formId,
	hideInternalButtons,
}) => {
	// Check if both CARD and SEPA are available
	const methods = availablePaymentMethods?.data || availablePaymentMethods || []
	const methodsArray = Array.isArray(methods) ? methods : []

	const hasCardMethod = methodsArray.some((method: any) => {
		const methodData = method?.data || method
		return methodData?.payment_type === 'CARD'
	})

	const hasSepaMethod = methodsArray.some((method: any) => {
		const methodData = method?.data || method
		return methodData?.payment_type === 'SEPA'
	})

	// If both CARD and SEPA are available, use StripeCardForm which has the selector
	const shouldUseCardFormWithSelector = hasCardMethod && hasSepaMethod

	// Use new CheckoutProvider approach when ONLY card payment method is available (no SEPA)
	if (
		cardPaymentMethodId &&
		paymentMethodType === 'card' &&
		!shouldUseCardFormWithSelector
	) {
		return (
			<StripeCheckoutForm
				accountId={accountId}
				billingSystemCountry={billingSystemCountry}
				paymentPlatformMethodId={cardPaymentMethodId}
				onSuccess={onSuccess}
				onCancel={onCancel}
				onSubmittingChange={onSubmittingChange}
				formId={formId}
				hideInternalButtons={hideInternalButtons}
			/>
		)
	}

	// Fallback to old Elements approach for SEPA or when both methods are available
	const commonProps = {
		onSubmit: handleStripeCardSubmit,
		onCancel,
		isSubmitting: false, // Webhook handles the save, no backend call needed
		availablePaymentMethods,
		isLoadingPaymentMethods,
		onSelectPaymentMethod: handleSelectPaymentMethod,
		isCreatingIntent: isCreatingCardIntent || isCreatingSepaIntent,
		hasClientSecret: !!clientSecret,
		selectedPaymentMethodType: selectedStripePaymentMethodType,
		clientSecretValue: clientSecret,
	}

	if (clientSecret && stripePromise) {
		// Keep old Elements approach for SEPA or when both methods available
		return (
			<Elements stripe={stripePromise} options={getElementsOptions()}>
				<StripeCardForm {...commonProps} />
			</Elements>
		)
	}

	return (
		<StripeCardForm
			{...commonProps}
			hasClientSecret={false}
			clientSecretValue={null}
		/>
	)
}
